import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useFinancialAnalytics } from '../../hooks/useFinancials';
import type {
  AnalyticsDimension,
  AnalyticsRow,
  LedgerPaymentMethod,
} from '../../lib/financialsApi';
import { Select } from '../ui/Select';
import { FadeIn, SkeletonTable } from '../ui/Skeleton';
import { formatCurrency } from '../../lib/format';
import { useI18n } from '../../contexts/I18nContext';

// ---------------------------------------------------------------------------
// "Analiză" tab (services nomenclature): income/expenses/profit aggregated by
// firm / department / service / client / supplier / person, from the four
// money sources (invoices, SmartBill associations, finalized bank
// reconciliation lines, payroll). Backend: GET /api/financials/analytics.
// ---------------------------------------------------------------------------

const DIMENSIONS: AnalyticsDimension[] = [
  'firm',
  'department',
  'service',
  'client',
  'supplier',
  'subscription',
  'person',
];

const CURRENT_YEAR = new Date().getUTCFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

type SortKey = 'label' | 'income' | 'expenses' | 'profit';

export function FinancialAnalytics({ companyIds }: { companyIds?: string[] } = {}) {
  const { t } = useI18n();
  const [dimension, setDimension] = useState<AnalyticsDimension>('service');
  const [year, setYear] = useState<number | undefined>(undefined);
  const [month, setMonth] = useState<number | undefined>(undefined);
  const [method, setMethod] = useState<LedgerPaymentMethod | undefined>(undefined);
  const [sortKey, setSortKey] = useState<SortKey>('income');
  const [sortAsc, setSortAsc] = useState(false);

  const query = useFinancialAnalytics(dimension, year, month, method, companyIds);

  const rows = useMemo(() => {
    const list = [...(query.data?.rows ?? [])];
    list.sort((a, b) => {
      const cmp =
        sortKey === 'label' ? a.label.localeCompare(b.label, 'ro') : a[sortKey] - b[sortKey];
      return sortAsc ? cmp : -cmp;
    });
    return list;
  }, [query.data, sortKey, sortAsc]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortAsc((v) => !v);
    } else {
      setSortKey(key);
      setSortAsc(key === 'label');
    }
  }

  function rowLabel(row: AnalyticsRow): string {
    return row.key === '__untagged__' ? t('financials.analytics.untagged') : row.label;
  }

  const sortIndicator = (key: SortKey) => (sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : '');

  const headerButton = (key: SortKey, labelKey: string, numeric: boolean) => (
    <button
      type="button"
      onClick={() => toggleSort(key)}
      className={`w-full font-medium text-gray-600 hover:text-gray-900 ${
        numeric ? 'text-right' : 'text-left'
      }`}
    >
      {t(labelKey)}
      {sortIndicator(key)}
    </button>
  );

  const totals = query.data?.totals;

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <Select
          label={t('financials.analytics.dimension')}
          value={dimension}
          onChange={(v) => setDimension(v as AnalyticsDimension)}
          options={DIMENSIONS.map((d) => ({
            value: d,
            label: t(`financials.analytics.dim.${d}`),
          }))}
          className="w-full sm:w-48"
        />
        <Select
          label={t('financials.yearLabel')}
          clearable
          value={year !== undefined ? String(year) : ''}
          onChange={(v) => {
            const next = v ? Number(v) : undefined;
            setYear(next);
            if (next === undefined) setMonth(undefined);
          }}
          placeholder={t('financials.allYears')}
          options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
          className="w-full sm:w-36"
        />
        <Select
          label={t('financials.monthLabel')}
          clearable
          value={month !== undefined ? String(month) : ''}
          onChange={(v) => setMonth(v ? Number(v) : undefined)}
          disabled={year === undefined}
          placeholder={t('financials.allMonths')}
          options={Array.from({ length: 12 }, (_, i) => i + 1).map((m) => ({
            value: String(m),
            label: t(`financials.month.${m}`),
          }))}
          className="w-full sm:w-40"
        />
        <Select
          label={t('paymentMethod.label')}
          clearable
          value={method ?? ''}
          onChange={(v) => setMethod((v || undefined) as LedgerPaymentMethod | undefined)}
          placeholder={t('paymentMethod.all')}
          options={(['BANK_TRANSFER', 'CARD', 'CASH'] as const).map((m) => ({
            value: m,
            label: t(`paymentMethod.${m}`),
          }))}
          className="w-full sm:w-40"
        />
      </div>

      {/* Table */}
      <div className="mt-4">
        {query.isLoading && <SkeletonTable rows={6} columns={4} />}
        {query.isError && <p className="text-red-600">{t('financials.analytics.error')}</p>}
        {query.data && rows.length === 0 && (
          <p className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500 shadow-sm">
            {t('financials.analytics.empty')}
          </p>
        )}
        {query.data && rows.length > 0 && (
          <FadeIn>
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2.5">
                      {headerButton('label', 'financials.analytics.col.label', false)}
                    </th>
                    <th className="px-4 py-2.5">
                      {headerButton('income', 'financials.analytics.col.income', true)}
                    </th>
                    <th className="px-4 py-2.5">
                      {headerButton('expenses', 'financials.analytics.col.expenses', true)}
                    </th>
                    <th className="px-4 py-2.5">
                      {headerButton('profit', 'financials.analytics.col.profit', true)}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => (
                    <tr key={row.key} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-gray-900">
                        <span className="inline-flex items-center gap-2">
                          {row.color && (
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: row.color }}
                            />
                          )}
                          {rowLabel(row)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                        {formatCurrency(row.income)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                        {formatCurrency(row.expenses)}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right font-medium tabular-nums ${
                          row.profit < 0 ? 'text-red-600' : 'text-emerald-700'
                        }`}
                      >
                        {formatCurrency(row.profit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {totals && (
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-4 py-2.5 font-semibold text-gray-900">
                        {t('financials.analytics.total')}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">
                        {formatCurrency(totals.income)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">
                        {formatCurrency(totals.expenses)}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right font-semibold tabular-nums ${
                          totals.profit < 0 ? 'text-red-600' : 'text-emerald-700'
                        }`}
                      >
                        {formatCurrency(totals.profit)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </FadeIn>
        )}
      </div>

      {/* Caveats — the aggregation mixes 4 heterogeneous money sources. */}
      {query.data && query.data.notes.length > 0 && (
        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <div className="flex items-center gap-2 font-medium">
            <Info className="h-4 w-4 shrink-0" />
            {t('financials.analytics.notesTitle')}
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-6">
            {query.data.notes.map((code) => (
              <li key={code}>{t(`financials.analytics.note.${code}`)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
